import { atom, PrimitiveAtom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import type { GunBarrelResponse } from 'components/charts/gun-barrel-chart/types';
import type { FormationSlot } from 'types/api-responses';
import type { UnitFormationSlotsResponse } from '../types';
import type { InventorySpacingValues } from '../type-curve-filters/atoms';
import type { SurfaceRecord } from './surface';

// Builds a `{ main, builder }` pair of fresh, independent atoms. Two fixed
// surfaces, so a static record beats `atomFamily` — full typing, no cache to
// evict, and `atoms[surface]` reads identically.
function surfacePair<T>(
  make: () => PrimitiveAtom<T>
): SurfaceRecord<PrimitiveAtom<T>> {
  return { main: make(), builder: make() };
}

// ─── Gun Barrel (per-surface, seedable) ─────────────────────────────────────

/**
 * The gun barrel chart data for one surface. A PLAIN atom (not
 * `createMutationAtom`) so the Unit Builder can be seeded by deep-copying the
 * Main Page's data on open — a mutation atom's backing store is private and only
 * writable by dispatching a request. `signature` is the request key the data was
 * fetched under, so a fetch hook can tell "do I already hold this selection's
 * data?" (and the seed can prove the copy is current).
 */
export interface GunBarrelSurfaceState {
  loading: boolean;
  data: GunBarrelResponse | null;
  error: string | null;
  signature: string | null;
}

export const EMPTY_GUN_BARREL_STATE: GunBarrelSurfaceState = {
  loading: false,
  data: null,
  error: null,
  signature: null,
};

export const gunBarrelStateAtoms = surfacePair<GunBarrelSurfaceState>(() =>
  atom(EMPTY_GUN_BARREL_STATE)
);

// ─── Slot Table (per-surface, seedable) ─────────────────────────────────────

/**
 * The slot-table data for one surface. `null` = unset (off an inventory surface,
 * the plain Type Curve page fetches per-table via `useFormationSlotsQuery`).
 * Carries the same `signature` idea as the gun barrel so the seed can be reused.
 */
export interface SlotTableSurfaceState {
  slots: FormationSlot[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  signature: string | null;
}

export const slotTableStateAtoms = surfacePair<SlotTableSurfaceState | null>(
  () => atom<SlotTableSurfaceState | null>(null)
);

// ─── Map slot geometry (per-surface, NOT seeded) ────────────────────────────

/**
 * Each surface's map slot-geometry copy (`POST /queries/inventory_unit_slots`).
 * Stays a mutation atom because the map always fetches its own geometry — it is
 * never deep-copied — and the builder map applies trim/recenter overlays the main
 * map must not see. Replaces the old `unitFormationSlotsMutationAtom` (main) +
 * `builderSlotGeometriesMutationAtom` (builder) pair.
 */
export const mapSlotGeomAtoms = {
  main: createMutationAtom<UnitFormationSlotsResponse, 'unitFormationSlots'>(),
  builder: createMutationAtom<
    UnitFormationSlotsResponse,
    'unitFormationSlots'
  >(),
};

// ─── Spacing edits (per-surface — replaces the back-channel) ─────────────────

/**
 * Live spacing/setback edits keyed by `inventoryFormationPackageId`, scoped to
 * one surface. The Unit Builder writes/reads its OWN
 * (`spacingEditsAtoms.builder`); the Main Page reads its own
 * (`spacingEditsAtoms.main`), which nothing writes — so the main gun barrel shows
 * the persisted `spacing_configs`, never the builder's unsaved edits. This
 * replaces the shared `inventorySpacingEditsAtom` back-channel that let a builder
 * edit reach into the main page.
 */
export const spacingEditsAtoms = surfacePair<
  Record<number, InventorySpacingValues>
>(() => atom<Record<number, InventorySpacingValues>>({}));

// ─── Deep copy Main → Builder on open ───────────────────────────────────────

/**
 * Seed the Builder surface from the Main surface when the Unit Builder opens.
 *
 * Called imperatively in the open handler (before the modal mounts) so the copy
 * lands before the builder's fetch effects run. When the Main Page already holds
 * gun barrel / slot-table data (settled, with a signature), deep-copy it so the
 * builder shows it instantly and its fetch hook — computing the same signature —
 * skips the redundant request. Otherwise leave the builder atoms empty so its
 * fetch hook fetches its own. Map geometry is never seeded (each map fetches).
 */
export const seedBuilderFromMainAtom = atom(null, (get, set) => {
  const gb = get(gunBarrelStateAtoms.main);
  set(
    gunBarrelStateAtoms.builder,
    gb.data && !gb.loading && gb.signature
      ? { ...gb, data: structuredClone(gb.data) }
      : EMPTY_GUN_BARREL_STATE
  );

  const st = get(slotTableStateAtoms.main);
  set(
    slotTableStateAtoms.builder,
    st && !st.loading && st.signature
      ? { ...st, slots: structuredClone(st.slots) }
      : null
  );
});

/** The cross-section (section-line) endpoints of a unit, as `TcSelectedDevArea`
 *  carries them. */
export interface SectionCrossEndpoints {
  start: { lat: number; lon: number };
  end: { lat: number; lon: number };
}

/**
 * The Unit Builder's OWN cross-section-line endpoints, recomputed as the user
 * reshapes the editable boundary (or drags the section line / rotates). The
 * builder's gun barrel + slot-table fetches key on THIS (falling back to the
 * shared selection's cross-section until the user edits), so a boundary edit
 * re-fetches both panes for the new section — without writing the shared
 * `tcSelectedDevAreaAtom` (which the main surface owns; see
 * `unit-builder-selection-single-writer`). `null` = no builder edit yet → use the
 * shared selection's cross-section. Cleared on unit change / close.
 */
export const builderSectionCrossAtom = atom<SectionCrossEndpoints | null>(null);

/**
 * The builder toolbar's "Surroundings" toggle.
 *
 * Held here rather than in the toolbar because `TcDevelopmentAreasLayer` owns
 * the dev-area layers and has to be the ONE writer of their visibility. It used
 * to be local state applied imperatively with `setLayoutProperty`, alongside the
 * layer's own declarative `visibility` from the map panel's "Type Curve → Units"
 * checkbox — two writers on the same property. Whichever ran last won, and
 * nothing re-ran the builder's exclusions, so toggling Units off and back on
 * could not be undone and brought back layers the builder hides.
 */
export const builderSurroundingsAtom = atom(true);

/** Clear the Builder surface's state — on close / unit change, so the next open
 *  re-seeds fresh instead of showing a stale unit's data. */
export const resetBuilderSurfaceAtom = atom(null, (_get, set) => {
  set(gunBarrelStateAtoms.builder, EMPTY_GUN_BARREL_STATE);
  set(slotTableStateAtoms.builder, null);
  set(spacingEditsAtoms.builder, {});
  set(builderSectionCrossAtom, null);
  set(builderSurroundingsAtom, true);
});
